import { PrimaryButton, PrimaryButtonProps, useModal } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router'

import { getCurrentQuote } from '../../../../domain'
import { usePromotion } from '../../../../hooks/covers'
import { useErrorHandlingAsyncCallback } from '../../../../hooks/errors'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { isCheckEnabled } from '../../../../state/configuration'
import { usePrincipal } from '../../../authentication'
import { IdCheckFailedModal } from '../../../organisms/proposal/IdCheckFailedModal'
import { useProduct } from '../../ProductProvider'
import { useStartCheckoutPath } from '../checkout'
import { ProposalPath } from '../ProposalPath'
import { useProposal } from '../ProposalProvider'

export interface ProposalContinueButtonProps extends PrimaryButtonProps {
  tKey: TKeyBuilder
  disabled?: boolean
}

export const ProposalContinueButton: FunctionComponent<ProposalContinueButtonProps> = ({
  tKey,
  disabled = false,
  ...props
}) => {
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()
  const modal = useModal()

  const navigate = useNavigate()

  const { proposal, commit, loading } = useProposal()
  const { product } = useProduct()

  const quote = getCurrentQuote(proposal)
  const checkoutPath = useStartCheckoutPath()
  const promotion = usePromotion(quote)

  const onButtonClick = useErrorHandlingAsyncCallback(async () => {
    await commit()

    const { isClear } = isCheckEnabled(product, 'id')
      ? await apiClient.idCheck(proposal.proposalCode)
      : { isClear: true }

    isClear
      ? navigate([ProposalPath.CHECKOUT, checkoutPath].join('/'))
      : modal.show({ content: <IdCheckFailedModal /> })
  })

  const buttonKey = promotion ? 'actions.active-promo' : 'actions.continue'

  return (
    <PrimaryButton
      data-testid='proposal-continue'
      onClick={onButtonClick.execute}
      disabled={disabled || loading}
      loading={onButtonClick.loading}
      {...props}
    >
      {t(tKey(buttonKey))}
    </PrimaryButton>
  )
}
